import { Box, Button, Paper, TextFieldProps, Theme, createStyles, makeStyles } from "@material-ui/core"
import DatePicker from "../../../components/DatePicker"
import { DateRangePreset, isSelectedDateRangePreset, useDateRangePresets } from "./use-date-range-presets"
import { useTranslation } from "react-i18next"

const DateRangeSelector = ({
  fromDate,
  toDate,
  firstDayOfWeek,
  onFromDateChange,
  onToDateChange,
  onDateRangeChange,
  embedded = false,
  showPresets = true,
}: DateRangeSelectorProps) => {
  const classes = useStyles()
  const { t } = useTranslation("hour-report")
  const today = new Date()
  const dateRangePresets = useDateRangePresets(firstDayOfWeek, {
    thisWeek: t("dateRange.thisWeek", { defaultValue: "This week" }),
    lastWeek: t("dateRange.lastWeek", { defaultValue: "Last week" }),
    thisMonth: t("dateRange.thisMonth", { defaultValue: "This month" }),
    lastMonth: t("dateRange.lastMonth", { defaultValue: "Last month" }),
  })
  const fromDateTextFieldProps: TextFieldProps = { label: t("dateRange.from", { defaultValue: "From" }), size: "small" }
  const toDateTextFieldProps: TextFieldProps = { label: t("dateRange.to", { defaultValue: "To" }), size: "small" }

  return (
    <Paper
      variant={embedded ? undefined : "outlined"}
      elevation={0}
      className={`${classes.dateRangePanel} ${embedded ? classes.embedded : ""}`}
    >
      <Box display="flex" alignItems="center">
        <Box flex={1} mr={2}>
          <DatePicker
            selected={fromDate}
            onChange={(date: Date | null) => date && onFromDateChange(date)}
            isClearable={false}
            dateFormat="MMM dd, yyyy"
            textFieldProps={fromDateTextFieldProps}
            calendarStartDay={firstDayOfWeek}
            maxDate={today}
          />
        </Box>
        <Box flex={1}>
          <DatePicker
            selected={toDate}
            onChange={(date: Date | null) => date && onToDateChange(date)}
            minDate={fromDate}
            isClearable={false}
            dateFormat="MMM dd, yyyy"
            textFieldProps={toDateTextFieldProps}
            calendarStartDay={firstDayOfWeek}
            maxDate={today}
          />
        </Box>
      </Box>
      <DateRangePresets
        presets={dateRangePresets}
        showPresets={showPresets}
        fromDate={fromDate}
        toDate={toDate}
        onDateRangeChange={onDateRangeChange}
      />
    </Paper>
  )
}

function DateRangePresets({
  presets,
  showPresets,
  fromDate,
  toDate,
  onDateRangeChange,
}: {
  presets: DateRangePreset[]
  showPresets: boolean
  fromDate: Date
  toDate: Date
  onDateRangeChange: (dateRange: DateRange) => void
}) {
  const classes = useStyles()

  if (!showPresets) {
    return null
  }

  return (
    <Box display="flex" flexWrap="nowrap" justifyContent={"space-between"} className={classes.dateRangePresets}>
      {presets.map((preset, index) => {
        const isSelected = isSelectedDateRangePreset(preset, { fromDate, toDate })

        return (
          <Box flex="1" marginRight={index < presets.length - 1 ? 1 : 0} key={preset.label}>
            <Button
              size="small"
              variant={isSelected ? "contained" : "outlined"}
              color={isSelected ? "primary" : "default"}
              onClick={() => onDateRangeChange({ fromDate: preset.fromDate, toDate: preset.toDate })}
              fullWidth
            >
              {preset.label}
            </Button>
          </Box>
        )
      })}
    </Box>
  )
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dateRangePanel: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      maxWidth: 520,
    },
    embedded: {
      padding: theme.spacing(2, 0, 0),
      marginBottom: 0,
      maxWidth: 520,
    },
    dateRangePresets: {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(-1),
    },
    dateRangePreset: {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  })
)

type DateRangeSelectorProps = DateRange & {
  firstDayOfWeek: DayOfWeek
  onFromDateChange: (date: Date) => void
  onToDateChange: (date: Date) => void
  onDateRangeChange: (dateRange: DateRange) => void
  embedded?: boolean
  showPresets?: boolean
}

type DateRange = {
  fromDate: Date
  toDate: Date
}

export default DateRangeSelector
